import React, { useState, useEffect } from "react"
import { Link } from "gatsby"
import DeurenLogo from "../../images/DeurenLogo"
import PrismicResponsiveImage from "src/components/PrismicResponsiveImage"
import useRafScrollState from "src/utils/useRafScrollState"
import Icon from "src/components/Icon"

const isSearchNavItem = item => {
  const label = item.label || ""
  const target = item.target || ""

  return target === "/search/" || /^(find anything|search)$/i.test(label)
}

const SearchIcon = ({ className = "" }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    aria-hidden
  >
    <path
      d="M6.41667 11.0833C8.994 11.0833 11.0833 8.994 11.0833 6.41667C11.0833 3.83934 8.994 1.75 6.41667 1.75C3.83934 1.75 1.75 3.83934 1.75 6.41667C1.75 8.994 3.83934 11.0833 6.41667 11.0833Z"
      stroke="currentColor"
      strokeLinejoin="round"
    />
    <path
      d="M12.25 12.25L9.71252 9.71252"
      stroke="currentColor"
      strokeLinejoin="round"
    />
  </svg>
)

const SearchNavLabel = ({ label }) => (
  <span className="inline-flex items-center align-middle leading-none">
    <SearchIcon className="mr-2 flex-none text-darkgrey transition duration-300 ease-in-out group-hover:text-gold" />
    {label}
  </span>
)

const Dropdown = props => {
  const [show, setShow] = useState(false)

  const handleClick = () => {
    setShow(false)
  }

  return (
    <div
      className={`group cursor-default flex-initial relative hover:text-gold focus:outline-none ${
        props.push_right ? "ml-auto" : ""
      }`}
      role="button"
      tabIndex={0}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      <div
        className={`border-transparent border-b transition-all duration-300 ease-in-out group-hover:border-gold cursor-default mx-3 -mb-px ${
          props.scrolled ? "py-4" : "py-6"
        }`}
      >
        <span className="font-body uppercase text-xsmenu tracking-widest font-bold">
          {isSearchNavItem(props) ? (
            <SearchNavLabel label={props.label} />
          ) : (
            props.label
          )}
        </span>
        <Icon
          className="fal fa-angle-down ml-1 align-middle text-gold"
          aria-hidden
        />
      </div>
      <div
        className={`fixed cursor-default left-0 w-screen bg-offwhite transition-all duration-500 ease-in-out transform origin-top ${
          show ? "scale-y-100 z-40 shadow" : "scale-y-0"
        } ${props.scrolled ? "top-[57px]" : "top-[101px]"}`}
      >
        <div
          className={`flex flex-row p-14 transition-opacity duration-500 ${
            show ? "opacity-100 delay-300" : "opacity-0 delay-0"
          } ease-in-out`}
        >
          <div className="flex-1 px-3">
            <ul className="list-none">
              <li className="uppercase text-xsmenu text-darkgrey cursor-default tracking-widest mb-4 font-body font-bold">
                {props.label}
              </li>
              {props.items.map((item, index) => {
                return (
                  <li className="mb-1" key={index}>
                    <Link
                      className="text-xs text-darkgrey font-body hover:text-gold transform transition-all duration-300 ease-in-out focus:outline-none"
                      to={`${item.target}`}
                      onClick={handleClick}
                      onTouchStart={handleClick}
                    >
                      {item.label}
                    </Link>
                  </li>
                )
              })}
            </ul>
          </div>
          <div className="flex-grow w-8/12">
            <h3 className="uppercase text-xsmenu text-darkgrey cursor-default tracking-widest mb-5 font-body font-bold px-3">
              {props.featured_title}
            </h3>
            <div className="flex">
              {props.featured_items.map((item, index) => {
                return (
                  <div className="flex-1 px-3 featured-item" key={index}>
                    <Link
                      className="text-darkgrey hover:text-gold transition duration-300 ease-in-out block relative"
                      to={`${item.target}`}
                      onClick={handleClick}
                      onTouchStart={handleClick}
                    >
                      <PrismicResponsiveImage
                        image={item.featured_image}
                        alt={item.label}
                        className="mb-3 w-full h-auto"
                        widths={[320, 640]}
                        sizes="20vw"
                      />
                      {item.play_button && (
                        <img
                          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 mb-3"
                          src={"/video.svg"}
                          alt="Video play button"
                        />
                      )}
                      <p className="text-xs font-body">{item.label}</p>
                    </Link>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const NavigationDesktop = props => {
  const [animate, setAnimate] = useState(true)
  const scrolled = useRafScrollState(({ scrollTop }) => scrollTop > 0)

  useEffect(() => {
    if (scrolled) {
      setAnimate(true)
      return undefined
    }

    const timeoutId = setTimeout(() => {
      setAnimate(false)
    }, 2000)

    return () => clearTimeout(timeoutId)
  }, [scrolled])

  return (
    <nav className="px-14 z-40 relative bg-white border-b border-grey-100 items-center hidden xl:flex">
      <Link
        className="absolute left-1/2 w-28 transform -translate-x-1/2"
        to="/"
        aria-label="Deuren Homepage"
      >
        <DeurenLogo
          className={`w-full overflow-visible ${animate ? "animate" : ""}`}
        />
      </Link>
      {props.items.map((navItem, index) => {
        return (
          <React.Fragment key={index}>
            {navItem.items.length ? (
              <Dropdown
                label={navItem.label}
                items={navItem.items}
                featured_items={navItem.featured_items}
                featured_title={navItem.featured_title}
                push_right={navItem.push_right}
                scrolled={scrolled}
              ></Dropdown>
            ) : (
              <Link
                className={`group flex-initial font-body uppercase text-xsmenu tracking-widest font-bold mx-3 -mb-px hover:text-gold transition duration-300 ease-in-out focus:outline-none ${
                  scrolled ? "py-4" : "py-6"
                } ${navItem.push_right ? "ml-auto" : ""}`}
                to={`${navItem.target}`}
              >
                {isSearchNavItem(navItem) ? (
                  <SearchNavLabel label={navItem.label} />
                ) : (
                  navItem.label
                )}
              </Link>
            )}
          </React.Fragment>
        )
      })}
    </nav>
  )
}

export default NavigationDesktop
